import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import {
  Box,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import React from "react";
import type {
  CouponCodeAssignedInstrument,
  CouponCodeAssignedUser,
  CouponCodeUsageType,
} from "./controller";

const TICKET_NOTCH_DIAMETER = 48;
const TICKET_NOTCH_RADIUS = TICKET_NOTCH_DIAMETER / 2;
const TICKET_BACKGROUND_COLOR = "#F2F9F8";

const MAX_VISIBLE_ASSIGNED_USERS = 3;
const MAX_VISIBLE_ASSIGNED_INSTRUMENTS = 3;

const buildAssignedUserLabels = (
  assignedUsers: CouponCodeAssignedUser[] | null,
  assignedUserUids: string[] | null
): string[] => {
  const formatUser = (user: CouponCodeAssignedUser): string | null => {
    if (!user) {
      return null;
    }
    const names = [user.firstName?.trim(), user.lastName?.trim()].filter(
      (value): value is string => Boolean(value)
    );
    const fullName = names.join(" ").trim();

    if (fullName && user.email) {
      return `${fullName} (${user.email})`;
    }

    if (user.email) {
      return user.email;
    }

    if (fullName) {
      return fullName;
    }

    return null;
  };

  const labels =
    assignedUsers
      ?.map((user) => formatUser(user))
      .filter((label): label is string => Boolean(label)) ?? [];

  if (labels.length > 0) {
    return labels;
  }

  const fallbackUids =
    assignedUserUids?.filter((uid) => Boolean(uid?.trim())) ?? [];

  if (fallbackUids.length > 0) {
    return fallbackUids;
  }

  return ["Uso livre"];
};

const buildAssignedInstrumentLabels = (
  assignedInstruments: CouponCodeAssignedInstrument[] | null,
  assignedInstrumentIds: number[] | null
): string[] => {
  const formatInstrument = (
    instrument: CouponCodeAssignedInstrument
  ): string | null => {
    if (!instrument) {
      return null;
    }
    const title = instrument.title?.trim();
    if (title) {
      return title;
    }
    if (Number.isFinite(instrument.id)) {
      return `Instrumento #${instrument.id}`;
    }
    return null;
  };

  const labels =
    assignedInstruments
      ?.map((instrument) => formatInstrument(instrument))
      .filter((label): label is string => Boolean(label)) ?? [];

  if (labels.length > 0) {
    return labels;
  }

  const fallbackIds =
    assignedInstrumentIds
      ?.map((id) => Number(id))
      .filter((id) => Number.isFinite(id) && id > 0)
      .map((id) => `Instrumento #${id}`) ?? [];

  if (fallbackIds.length > 0) {
    return fallbackIds;
  }

  return ["Uso livre"];
};

interface CouponCodeCardProps {
  code: string;
  active: boolean;
  assignedUserUids: string[] | null;
  assignedUsers: CouponCodeAssignedUser[] | null;
  assignedInstrumentIds: number[] | null;
  assignedInstruments: CouponCodeAssignedInstrument[] | null;
  usageLimit: number | null;
  usageType: CouponCodeUsageType;
  redemptionCount: number;
  validityLabel: string;
  createdAtLabel: string;
  updatedAtLabel: string;
  onEdit?: () => void;
  onDelete?: () => void;
  editLabel?: string;
  deleteLabel?: string;
  editDisabled?: boolean;
  deleteDisabled?: boolean;
  editLoading?: boolean;
  deleteLoading?: boolean;
}

export const CouponCodeCard: React.FC<CouponCodeCardProps> = ({
  code,
  active,
  assignedUserUids,
  assignedUsers,
  assignedInstrumentIds,
  assignedInstruments,
  usageLimit,
  usageType,
  redemptionCount,
  validityLabel,
  createdAtLabel,
  updatedAtLabel,
  onEdit,
  onDelete,
  editLabel = "Editar",
  deleteLabel = "Excluir",
  editDisabled = false,
  deleteDisabled = false,
  editLoading = false,
  deleteLoading = false,
}) => (
  <Paper
    elevation={0}
    sx={{
      position: "relative",
      height: '-webkit-fill-available',
      px: 3,
      py: 3,
      borderRadius: 4,
      background: "linear-gradient(145deg, #ffffff 0%, #F6FCFA 100%)",
      overflow: "hidden",
      border: "none",
      "&::before": {
        content: '""',
        position: "absolute",
        top: "50%",
        left: -TICKET_NOTCH_RADIUS,
        width: TICKET_NOTCH_DIAMETER,
        height: TICKET_NOTCH_DIAMETER,
        borderRadius: "50%",
        backgroundColor: TICKET_BACKGROUND_COLOR,
        border: `1px solid ${grey[300]}`,
        transform: "translateY(-50%)",
        zIndex: 2,
      },
      "&::after": {
        content: '""',
        position: "absolute",
        top: "50%",
        right: -TICKET_NOTCH_RADIUS,
        width: TICKET_NOTCH_DIAMETER,
        height: TICKET_NOTCH_DIAMETER,
        borderRadius: "50%",
        backgroundColor: TICKET_BACKGROUND_COLOR,
        border: `1px solid ${grey[300]}`,
        transform: "translateY(-50%)",
        zIndex: 2,
      },
      "& .ticket-border": {
        position: "absolute",
        inset: 0,
        borderRadius: 4,
        border: `1px solid ${grey[300]}`,
        pointerEvents: "none",
        zIndex: 0,
      },
      "& .ticket-notch-mask": {
        position: "absolute",
        top: `calc(50% - ${TICKET_NOTCH_RADIUS}px)`,
        width: TICKET_NOTCH_RADIUS + 2,
        height: TICKET_NOTCH_DIAMETER,
        background: "linear-gradient(145deg, #ffffff 0%, #F6FCFA 100%)",
        pointerEvents: "none",
        zIndex: 1,
      },
      "& .ticket-notch-mask--left": {
        left: -1,
      },
      "& .ticket-notch-mask--right": {
        right: -1,
      },
    }}
  >
    <Box className="ticket-border" />
    <Box className="ticket-notch-mask ticket-notch-mask--left" />
    <Box className="ticket-notch-mask ticket-notch-mask--right" />
    <Stack spacing={2} px={1}>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        gap={2}
        flexWrap="wrap"
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography
            variant="h6"
            fontWeight={700}
            color="#1EA097"
            sx={{ wordBreak: "break-word" }}
          >
            {code.toLowerCase()}
          </Typography>
          <Chip
            label={active ? "Ativo" : "Inativo"}
            size="small"
            color={active ? "success" : "default"}
            variant={active ? "filled" : "outlined"}
            sx={{
              fontWeight: 600,
              borderRadius: 999,
            }}
          />
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          {onEdit && (
            <Tooltip title={editLabel} disableInteractive>
              <Box component="span">
                <IconButton
                  size="small"
                  onClick={onEdit}
                  disabled={editDisabled}
                  color="primary"
                  aria-label={editLabel}
                >
                  {editLoading ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : (
                    <EditOutlinedIcon fontSize="small" />
                  )}
                </IconButton>
              </Box>
            </Tooltip>
          )}
          {onDelete && (
            <Tooltip title={deleteLabel} disableInteractive>
              <Box component="span">
                <IconButton
                  size="small"
                  onClick={onDelete}
                  disabled={deleteDisabled || active}
                  color="error"
                  aria-label={deleteLabel}
                >
                  {deleteLoading ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : (
                    <DeleteOutlineIcon fontSize="small" />
                  )}
                </IconButton>
              </Box>
            </Tooltip>
          )}
        </Stack>
      </Box>

      <Box display="flex" gap={2} flexWrap="wrap" justifyContent="space-between">
        <Box>
          <Typography variant="caption" color="text.secondary" fontWeight={500}>
            Vigência
          </Typography>
          <Typography variant="body2">
            {validityLabel}
          </Typography>
        </Box>

        <Box>
          <Typography variant="caption" color="text.secondary" fontWeight={500}>
            Utilizações
          </Typography>
          <Typography variant="body2">
            {usageType === "user"
              ? "1 por usuário"
              : usageLimit === null
              ? `${redemptionCount}`
              : `${redemptionCount} / ${usageLimit}`}
          </Typography>
        </Box>
      </Box>

      <Divider
        sx={{
          borderStyle: "dashed",
          borderColor: "rgba(30,160,151,0.3)",
        }}
      />

      <Box
        display="flex"
        gap={2}
        flexWrap="wrap"
        justifyContent="space-between"
      >
        <Box width={'100%'}>
          <Typography variant="caption" color="text.secondary" fontWeight={500}>
            Usuários vinculados
          </Typography>
          <Box display="flex" flexWrap={'wrap'} gap={0.5} mt={0.5} >
            {(() => {
              const labels = buildAssignedUserLabels(
                assignedUsers,
                assignedUserUids
              );
              const visibleLabels = labels.slice(0, MAX_VISIBLE_ASSIGNED_USERS);
              const overflowLabels = labels.slice(MAX_VISIBLE_ASSIGNED_USERS);

              return (
                <>
                  {visibleLabels.map((label, index) => (
                    <Chip
                      key={`${label}-${index}`}
                      size="small"
                      label={label}

                    />
                  ))}
                  {overflowLabels.length > 0 && (
                    <Tooltip
                      title={
                        <Box
                          display="flex"
                          flexDirection="column"
                          gap={.75}
                          maxHeight={240}
                          maxWidth={360}
                          overflow="auto"
                          sx={{
                            py: 1,
                            px: 1,
                            backgroundColor: 'background.paper'
                          }}

                        >
                          {overflowLabels.map((label, index) => (

                            <Chip
                              key={`${label}-${index}`}
                              size="small"
                              label={label}
                            />
                          ))}
                        </Box>
                      }
                      arrow
                    >
                      <Typography
                        variant="body2"
                        color="primary"
                        sx={{ cursor: "pointer" }}
                      >
                        +{overflowLabels.length}{" "}
                        {overflowLabels.length === 1 ? "usuário" : "usuários"}
                      </Typography>
                    </Tooltip>
                  )}
                </>
              );
            })()}
          </Box>
        </Box>
        <Box width={'100%'}>
          <Typography variant="caption" color="text.secondary" fontWeight={500}>
            Instrumentos vinculados
          </Typography>
          <Box display="flex" flexWrap={'wrap'} gap={0.5} mt={0.5}>
            {(() => {
              const labels = buildAssignedInstrumentLabels(
                assignedInstruments,
                assignedInstrumentIds
              );
              const visibleLabels = labels.slice(
                0,
                MAX_VISIBLE_ASSIGNED_INSTRUMENTS
              );
              const overflowLabels = labels.slice(
                MAX_VISIBLE_ASSIGNED_INSTRUMENTS
              );

              return (
                <>
                  {visibleLabels.map((label, index) => (
                    <Chip key={`${label}-${index}`} size="small" label={label} />
                  ))}
                  {overflowLabels.length > 0 && (
                    <Tooltip
                      title={
                        <Box
                          display="flex"
                          flexDirection="column"
                          gap={0.75}
                          maxHeight={240}
                          maxWidth={360}
                          overflow="auto"
                          sx={{
                            py: 1,
                            px: 1,
                            backgroundColor: "background.paper",
                          }}
                        >
                          {overflowLabels.map((label, index) => (
                            <Chip
                              key={`${label}-${index}`}
                              size="small"
                              label={label}
                            />
                          ))}
                        </Box>
                      }
                      arrow
                    >
                      <Typography
                        variant="body2"
                        color="primary"
                        sx={{ cursor: "pointer" }}
                      >
                        +{overflowLabels.length}{" "}
                        {overflowLabels.length === 1
                          ? "instrumento"
                          : "instrumentos"}
                      </Typography>
                    </Tooltip>
                  )}
                </>
              );
            })()}
          </Box>
        </Box>

        <Box>
          <Typography variant="caption" color="text.secondary" fontWeight={500}>
            Atualizado em
          </Typography>
          <Typography variant="body2">{updatedAtLabel}</Typography>
        </Box>
      </Box>
    </Stack>
  </Paper>
);
